import de from 'date-fns/locale/de'
import React, { ReactElement } from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import { Controller, Control, FieldErrors, UseFormRegister, RegisterOptions } from 'react-hook-form'
import ReactTooltip from 'react-tooltip'
registerLocale('de', de)

const Component = (props: {
  id: string
  label?: string
  info?: string
  className?: string
  disabled?: boolean | undefined
  errors?: FieldErrors
  customMessage?: string;
  register: UseFormRegister<any>
  validations?: RegisterOptions<any>
  peekNextMonth?: boolean | undefined
  showMonthDropdown?: boolean | undefined
  showYearDropdown?: boolean | undefined
  dropdownMode?: 'select' | 'scroll' | undefined
  maxDate?: Date | null | undefined
  minDate?: Date | null | undefined
  dateFormat?: string | string[] | undefined
  control: Control<any>
}): ReactElement => {
  const {
    id,
    label,
    info,
    className,
    errors,
    customMessage,
    disabled,
    register,
    validations,
    peekNextMonth,
    showMonthDropdown,
    showYearDropdown,
    dropdownMode,
    maxDate,
    minDate,
    dateFormat,
    control
  } = props

  const classes = [
    'react-form__form-item',
    'form-input',
    errors && errors[id]
      ? 'error'
      : ''
  ]
    .filter(Boolean)
    .join(' ')

  let required = false
  if (validations) {
    required = Object.keys(validations).filter(key => key === 'required').length > 0
  }

  return (
    <div
      className={`react-form__form-group form-item form-item--datepicker
        ${className}`}
    >
      <div className="relative">
        <label
          htmlFor={id}
          className={`${required ? 'form-required' : ''}`}
        >
          {label}
        </label>
        <div
          className={`relative ${
            info ? 'form-group--with-info' : ''
          }`}
        >
              <Controller
                control={control}
                defaultValue={null}
                {...register(id, validations)}
                render={({ field }) => (
                  <DatePicker
                      locale="de"
                      {...field}
                      disabled={disabled}
                      autoComplete="off"
                      customInput={<input type="text" inputMode={'none'} />}
                      name={id}
                      id={id}
                      className={classes}
                      selected={field.value}
                      dateFormat={
                        dateFormat || 'dd.MM.yyyy'
                      }
                      popperPlacement="bottom"
                      placeholderText={'Datum auswählen'}
                      peekNextMonth={peekNextMonth}
                      showMonthDropdown={showMonthDropdown}
                      showYearDropdown={showYearDropdown}
                      dropdownMode={dropdownMode}
                      maxDate={maxDate}
                      minDate={minDate}
                      onChange={(date, event) => {
                        const target = event?.target as HTMLInputElement

                        // Check for dates like 1.5.21 or 1.5.2021
                        const regex = /^\d{1,2}\.\d{1,2}\.\d{2,4}$/

                        if (regex.test(target.value)) {
                          const [day, month, year] = target.value.split('.').map(Number)

                          // Assume a default year prefix if necessary
                          const fullYear = year < 100 ? (year + 2000) : year

                          // JS months start from 0
                          const newDate = new Date(fullYear, month - 1, day)

                          // Apply the changes to the form and the component
                          field.onChange(newDate)
                        } else {
                          // If the input doesn't match the expected format, fall back to the original behavior
                          field.onChange(date)
                        }
                      }}
                  />
                )}
              />
          </div>
          {info && (
            <div>
              <div className="form-group__info-icon absolute">
                <i
                  className="info-icon"
                  data-tip
                  data-for={id}
                  data-place="bottom"
                  data-event="click focus"
                />
              </div>
              <ReactTooltip
                id={id}
                effect="solid"
                globalEventOff="click"
              >
                <div className="form-group__info absolute active">
                  {info}
                </div>
              </ReactTooltip>
            </div>
          )}
        {errors && errors[id] &&
          <div className="mt-2 text-sm form-item--error-message">
            {errors[id]?.message}
          </div>
        }
        {customMessage && customMessage.length > 0 && (
          <div className="mt-2 text-sm text-warning">
            {customMessage}
          </div>
        )}
      </div>
    </div>
  )
}

export default Component
